/**
 * FP3-051 — Restore Student modal.
 *
 * Reverse of ArchiveStudentsModal. Single-row only in this pass
 * (bulk restore is deferred per the FP3-051 scope agreement).
 *
 * Permissions are enforced server-side: super_admin or
 * admin/org_admin within their own org. Providers/assistants/eval
 * are 403 from `require_admin`. The modal does not show for those
 * roles (parent controls visibility via the `canArchive` flag).
 *
 * Restore preserves the full archive audit trail (exit_reason,
 * exited_at, archived_by, archive_reason all remain) and adds
 * restored_at + restored_by + restore_note on top.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RestoreStudentModal({
  open,
  onOpenChange,
  studentId,
  studentName,
  onRestored,
}) {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNote("");
  }, [open]);

  const submit = async () => {
    if (!studentId) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/admin/students/${studentId}/restore`,
        { restore_note: note },
      );
      toast.success("Student restored to active");
      if (typeof onRestored === "function") onRestored();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not restore — check your permissions and try again"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[460px]"
        data-testid="restore-student-modal"
      >
        <DialogHeader>
          <DialogTitle>Restore to active</DialogTitle>
          <DialogDescription>
            {studentName ? (
              <>
                <span className="font-medium text-[#1F2937]">{studentName}</span>{" "}
                will return to active. The previous archive reason and
                history are preserved.
              </>
            ) : (
              <>
                This student will return to active. The previous archive
                reason and history are preserved.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label
              htmlFor="restore-note"
              className="text-[12.5px] font-semibold"
            >
              Restore note (optional)
            </Label>
            <Textarea
              id="restore-note"
              data-testid="restore-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add context — visible in audit history (e.g. parent re-enrolled)..."
              maxLength={500}
              className="text-[13px]"
            />
            <div className="text-[10.5px] text-[#9AA0A6] text-right">
              {note.length}/500
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="restore-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-[#1E5A3F] hover:bg-[#174731] text-white"
            data-testid="restore-confirm-btn"
          >
            {submitting ? "Restoring..." : "Restore to active"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
